/**
 * Workflow Definition Manager Component
 *
 * Purpose: Manages workflow definitions with CRUD operations, filtering, and validation.
 *          Supports both GLOBAL and tenant-specific workflow definitions.
 *
 * Architecture: Table-based interface using MDTable component with modal forms
 *              for create/edit operations. Integrates with workflow service.
 *
 * Dependencies: React, Material-UI, MDTable, WorkflowService
 * Author: Development Team
 * Created: 2026-04-13
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Public as GlobalIcon,
  Business as TenantIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';

import MDTable from '../md-table';
import { getScopeChipColor } from '../../constants/mdm';
import { TableColumn } from '../../types/table';
import workflowService, {
  WorkflowDefinition,
  WorkflowDefinitionFormData,
} from '../../services/workflowService';
import { TenantInfo } from '../../services/metadataService';
import { formatAuditTimestamp, formatAuditUser } from '../../utils/formatting';
import WorkflowDefinitionModal from './modals/WorkflowDefinitionModal';
import { RowActionButton, RowActions, StatusChip } from '../common';
import { SecondaryButton } from '../common/ActionButtons';

// Component interfaces
interface WorkflowDefinitionManagerProps {
  tenantContext: TenantInfo;
  canEdit: boolean;
  userPermissions: {
    can_edit_global: boolean;
    can_edit_tenant: boolean;
    can_switch_context: boolean;
  };
  onNavigateToSteps?: (workflowId: string, workflowLabel?: string) => void;
}

/**
 * Workflow Definition Manager Component
 */
const WorkflowDefinitionManager: React.FC<WorkflowDefinitionManagerProps> = ({
  tenantContext,
  canEdit,
  userPermissions: _userPermissions,
  onNavigateToSteps,
}) => {
  // State management
  const [data, setData] = useState<WorkflowDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedWorkflowDef, setSelectedWorkflowDef] = useState<WorkflowDefinition | undefined>();

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingWorkflowDef, setDeletingWorkflowDef] = useState<WorkflowDefinition | null>(null);

  // Revert confirmation state
  const [revertDialogOpen, setRevertDialogOpen] = useState(false);
  const [revertingWorkflowDef, setRevertingWorkflowDef] = useState<WorkflowDefinition | null>(null);
  const [revertLoading, setRevertLoading] = useState(false);

  // Filters
  const [hideGlobal, setHideGlobal] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await workflowService.getWorkflowDefinitions({
        tenant_id: tenantContext.tenant_id,
      });
      setData(response || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load workflow definitions');
    } finally {
      setLoading(false);
    }
  }, [tenantContext.tenant_id]);

  // Filter data based on hideGlobal setting
  const filteredData = useMemo(() => {
    if (hideGlobal) {
      return data.filter((item) => item.tenant_id !== 'GLOBAL');
    }
    return data;
  }, [data, hideGlobal]);

  // Table rows, grouped by Model once there is more than one (ORCH-225). With
  // a single model (mediaplan today) the list stays flat and Models is a
  // regular column; with several, MDTable's collapsible groups take over, the
  // same way Workflow Steps groups by Workflow Definition. A definition's
  // group is its model list, so one spanning two models is its own group.
  const { rows, groupByModel } = useMemo(() => {
    const tableRows = filteredData.map((item) => ({
      ...item,
      id: `${item.workflow_id}-${item.tenant_id}`,
      model_group: item.models && item.models.length > 0 ? item.models.join(', ') : '—',
    }));
    const grouped = new Set(tableRows.map((row) => row.model_group)).size > 1;
    if (grouped) {
      // Collapsible groups expect each group's rows to be contiguous; the
      // definitions with no model go last
      const unmodelled = (row: { models?: string[] }) => (row.models?.length ? 0 : 1);
      tableRows.sort(
        (a, b) =>
          unmodelled(a) - unmodelled(b) ||
          a.model_group.localeCompare(b.model_group) ||
          a.label.localeCompare(b.label)
      );
    }
    return { rows: tableRows, groupByModel: grouped };
  }, [filteredData]);

  // Create custom toolbar filters
  const customToolbarFilters = useMemo(
    () => (
      <FormControlLabel
        control={
          <Checkbox
            checked={hideGlobal}
            onChange={(e) => setHideGlobal(e.target.checked)}
            data-testid="hide-global-checkbox"
          />
        }
        label="Hide GLOBAL"
        sx={{
          '& .MuiFormControlLabel-label': {
            fontSize: '0.875rem',
            color: 'text.secondary',
          },
        }}
      />
    ),
    [hideGlobal]
  );

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Table columns configuration: Name first (it is the record link, ORCH-225),
  // then Models, Steps and Scope; Workflow ID and Description stay in the
  // Columns menu but are hidden by default.
  const columns: TableColumn[] = [
    {
      field: 'label',
      headerName: 'Name',
      width: 200,
      sortable: true,
      renderCell: (value: string) => <Typography variant="body2">{value}</Typography>,
    },
    {
      field: 'models',
      headerName: 'Models',
      width: 200,
      sortable: false,
      renderCell: (value: string[]) => (
        <Typography variant="body2" color="textSecondary">
          {value && value.length > 0 ? value.join(', ') : '—'}
        </Typography>
      ),
    },
    {
      field: 'step_count',
      headerName: 'Steps',
      width: 100,
      sortable: true,
      renderCell: (value: number, row: WorkflowDefinition) => {
        const count = value ?? 0;
        return (
          <Chip
            label={count}
            size="small"
            color={count > 0 ? 'primary' : 'default'}
            onClick={(e) => {
              e.stopPropagation();
              if (onNavigateToSteps) {
                onNavigateToSteps(row.workflow_id, row.label);
              }
            }}
            sx={{ cursor: 'pointer' }}
          />
        );
      },
    },
    {
      field: 'tenant_id',
      headerName: 'Scope',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: WorkflowDefinition) => {
        const scopeInfo = (row as any).scope_info;
        let scopeLabel = '';
        let scopeIcon = <TenantIcon fontSize="small" />;

        if (scopeInfo) {
          scopeLabel = scopeInfo.scope;
          if (scopeLabel === 'Overridden') {
            scopeLabel = 'OVERRIDDEN';
          }
          scopeIcon =
            scopeLabel === 'GLOBAL' ? (
              <GlobalIcon fontSize="small" />
            ) : (
              <TenantIcon fontSize="small" />
            );
        } else {
          if (value === 'GLOBAL') {
            scopeLabel = 'GLOBAL';
            scopeIcon = <GlobalIcon fontSize="small" />;
          } else {
            scopeLabel = 'LOCAL';
            scopeIcon = <TenantIcon fontSize="small" />;
          }
        }

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {scopeIcon}
            <Chip
              label={scopeLabel}
              size="small"
              color={getScopeChipColor(scopeLabel)}
              variant="outlined"
            />
          </Box>
        );
      },
    },
    {
      field: 'workflow_id',
      headerName: 'Workflow ID',
      width: 200,
      sortable: true,
      renderCell: (value: string) => (
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }} data-testid="workflow-id-cell">
          {value}
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 250,
      sortable: true,
      renderCell: (value: string) => (
        <Typography variant="body2" color="textSecondary">
          {value || '\u2014'}
        </Typography>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Modified Date',
      width: 140,
      sortable: true,
      renderCell: (value: string) => formatAuditTimestamp(value),
    },
    {
      field: 'updated_by',
      headerName: 'Modified By',
      width: 120,
      sortable: true,
      renderCell: (value: string, row: WorkflowDefinition) =>
        formatAuditUser(value, row.updated_by_user),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      sortable: true,
      renderCell: (value: string) => <StatusChip status={value} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (_value: any, row: WorkflowDefinition) => (
        <RowActions
          entityLabel="Workflow Definition"
          rowLabel={row.label}
          testIdPrefix="workflow-definition"
          rowTestId={`workflow-definition-row-${row.workflow_id}`}
          onEdit={() => handleEdit(row)}
          secondaryActions={
            row.scope_info?.scope === 'Overridden' && (
              <RowActionButton
                label="Revert to Global Workflow Definition"
                testId="workflow-definition-revert"
                onClick={() => handleRevertWorkflowDef(row)}
                disabled={!canEdit}
                icon={<RestoreIcon fontSize="small" color={canEdit ? 'warning' : 'disabled'} />}
              />
            )
          }
          onDelete={() => handleDeleteClick(row)}
          deleteDisabled={!canEdit}
        />
      ),
    },
  ];

  // Event handlers
  const handleCreate = () => {
    setSelectedWorkflowDef(undefined);
    setModalMode('create');
    setModalOpen(true);
  };

  const handleEdit = (workflowDef: WorkflowDefinition) => {
    setSelectedWorkflowDef(workflowDef);
    setModalMode('edit');
    setModalOpen(true);
  };

  const handleRowClick = (row: WorkflowDefinition) => {
    handleEdit(row);
  };

  const handleSave = async (formData: WorkflowDefinitionFormData) => {
    if (modalMode === 'create') {
      await workflowService.createWorkflowDefinition(formData);
    } else if (selectedWorkflowDef) {
      await workflowService.updateWorkflowDefinition(
        selectedWorkflowDef.workflow_id,
        selectedWorkflowDef.tenant_id,
        formData,
        tenantContext.tenant_id
      );
    }
    loadData();
  };

  // Delete handlers
  const handleDeleteClick = (workflowDef: WorkflowDefinition) => {
    setDeletingWorkflowDef(workflowDef);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingWorkflowDef) return;
    try {
      await workflowService.deleteWorkflowDefinition(
        deletingWorkflowDef.workflow_id,
        deletingWorkflowDef.tenant_id
      );
      setDeleteDialogOpen(false);
      setDeletingWorkflowDef(null);
      await loadData();
    } catch (err) {
      console.error('Failed to delete workflow definition:', err);
    }
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeletingWorkflowDef(null);
  };

  // Revert handlers
  const handleRevertWorkflowDef = (workflowDef: WorkflowDefinition) => {
    setRevertingWorkflowDef(workflowDef);
    setRevertDialogOpen(true);
  };

  const handleRevertConfirm = useCallback(async () => {
    if (!revertingWorkflowDef) return;

    setRevertLoading(true);
    try {
      await workflowService.revertWorkflowDefinition(
        revertingWorkflowDef.workflow_id,
        revertingWorkflowDef.tenant_id
      );
      setRevertDialogOpen(false);
      setRevertingWorkflowDef(null);
      await loadData();
    } catch (err) {
      console.error('Failed to revert workflow definition:', err);
    } finally {
      setRevertLoading(false);
    }
  }, [revertingWorkflowDef, loadData]);

  const handleRevertClose = useCallback(() => {
    setRevertDialogOpen(false);
    setRevertingWorkflowDef(null);
    setRevertLoading(false);
  }, []);

  return (
    <Box>
      {/* Data Table */}
      <Box>
        <MDTable
          mode="manual"
          manual={{
            columns,
            title: 'Workflow Definitions',
            description: `${filteredData.length} workflow definition(s) found`,
          }}
          data={rows}
          loading={loading}
          error={error}
          onRowClick={handleRowClick}
          onRefresh={loadData}
          onAddNew={canEdit ? handleCreate : undefined}
          showRefreshButton={true}
          showAddButton={canEdit}
          selectable={false}
          defaultSortModel={[{ field: 'label', sort: 'asc' }]}
          sortable={true}
          filterable={true}
          groupByField={groupByModel ? 'model_group' : undefined}
          collapsibleGroups={groupByModel}
          groupLabelField="model_group"
          groupColumnHeaderName="Model"
          customToolbarFilters={customToolbarFilters}
          defaultColumnVisibility={{
            workflow_id: false,
            description: false,
          }}
        />
      </Box>

      {/* Create/Edit Modal */}
      <WorkflowDefinitionModal
        open={modalOpen}
        mode={modalMode}
        workflowDefinition={selectedWorkflowDef}
        tenantContext={tenantContext}
        canEdit={canEdit}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        onDelete={async (workflowDef) => {
          handleDeleteClick(workflowDef);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Delete Workflow Definition</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the workflow definition "{deletingWorkflowDef?.label}"?
            This action cannot be undone and will also remove all associated workflow steps.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleDeleteClose}>Cancel</SecondaryButton>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert Confirmation Dialog */}
      <Dialog open={revertDialogOpen} onClose={handleRevertClose}>
        <DialogTitle>Revert to Global Definition</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revert the workflow definition "{revertingWorkflowDef?.label}"
            to the global definition? Your tenant-specific overrides will be removed.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <SecondaryButton onClick={handleRevertClose} disabled={revertLoading}>
            Cancel
          </SecondaryButton>
          <Button
            onClick={handleRevertConfirm}
            color="warning"
            variant="contained"
            disabled={revertLoading}
          >
            {revertLoading ? 'Reverting...' : 'Revert'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkflowDefinitionManager;
